import { useState, useRef, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { setUpperPopup } from '../../store/reducers/general'
import { getNextMembers } from '../../services/util'
import { useNavigate } from 'react-router-dom'
import { videoService } from '../../services/video'
import { uploadFile } from '../../services/upload'
import { VideoPopup } from './popup'
import { NoThumbnail } from '../no-thumbnail.jsx'

let files = {
    0: '',
    1: '',
    2: ''
}

export function IdeaMaker({ isAdmin, isEditable, part, status, isThumbnailEditor, createdBy, videoId, isOpen }) {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { currentTeam, _id } = useSelector(state => state.user)
    const original = useRef(JSON.parse(JSON.stringify(part)))
    const [edited, setEdited] = useState(part)
    const [popup, setPopup] = useState(false)
    const [changeFlag, setChangeFlag] = useState(false)
    const nextMembers = useRef(getNextMembers(currentTeam, _id, 'ideaMaker'))
    const nextMember = useRef(null)

    useEffect(() => {
        files[0] = part.thumbnails[0] || ''
        files[1] = part.thumbnails[1] || ''
        files[2] = part.thumbnails[2] || ''
    }, [])

    const saveThumbnails = async (submit = false) => {
        try {
            const thumbnails = await getThumbnailsUrls()
            await videoService.saveThumbnails(thumbnails, videoId, currentTeam._id, submit)
            dispatch(setUpperPopup('thumbnails-edited'))
            setChangeFlag(false)
            if (submit) navigate('/')
        }
        catch (error) {
            console.log(error)
            dispatch(setUpperPopup('errorServer'))
        }
    }

    const onImageChange = (ev) => {
        const id = Number(ev.target.id)
        if (!ev.target.files[0]) return
        if (!ev.target.files[0].type.startsWith('image/')) return dispatch(setUpperPopup('image-only'))
        files[id] = ev.target.files[0]
        setEdited({ ...edited, thumbnails: edited.thumbnails.map((thumbnail, idx) => idx === id ? URL.createObjectURL(ev.target.files[0]) : thumbnail) })
        setChangeFlag(true)
    }

    const editTitle = (ev) => {
        const { name, value } = ev.target
        let titles = edited.titles
        if (value === '' && name !== '2') {
            if (name === '0' && edited.titles[1] && edited.titles[2]) titles = [edited.titles[1], edited.titles[2], '']
            else if (name === '0' && edited.titles[1]) titles = [edited.titles[1], '', '']
            else if (name === '1' && edited.titles[2]) titles = [edited.titles[0], edited.titles[2], '']
            else titles[name] = ''
        }
        else titles[name] = value
        setEdited({ ...edited, titles })
    }

    const buttons = useMemo(() => {
        if (!status.thumbnailsSubmitted && isThumbnailEditor) return <div className='buttons-wrapper'>
            <p>הגש לבדיקה</p>
            <div className='buttons'>
                {changeFlag && <>
                    <div onClick={() => setPopup('discard-changes')} className='gray'>בטל שינויים</div>
                    <div className='save' onClick={() => setPopup('save-thumbnails')}>שמור טאמבניילים</div>
                </>}
                <div className='green' onClick={() => setPopup('finish-thumbnails')}> הגש טאמבניילים</div>
            </div>
        </div>
        const buttons = []
        if (!isEditable) return null
        if (isEditable && changeFlag) {
            buttons.push(<div onClick={() => setPopup('discard-changes')} key='default' className='gray'>בטל שינויים</div>)
            buttons.push(<div onClick={() => setPopup('save-only')} key='save-only' className='save'>שמור שינויים</div>)
        }
        if (!isAdmin && isEditable) {
            buttons.push(<div onClick={() => setPopup('forward-as-success')} className='green' key='forward-as-success'>העבר כהצלחה</div>)
            buttons.push(<div onClick={() => setPopup('forward-as-failure')} className='red' key='forward-as-failure'>העבר ככשלון</div>)
        }
        if (isAdmin && status.level === 'ideaMaker') {
            if (createdBy.role === 'ideaMaker') buttons.push(<div onClick={() => setPopup('returned-for-changes')} key='reject' className='red' name='returned-for-changes'>החזר אחורה לתיקון</div>)
            const next = <div className='next-wrapper' key='next'>
                <select ref={nextMember}>
                    {nextMembers.current.map(member => <option value={member._id} key={member._id}>{member.name}</option>)}
                </select>
                <div onClick={() => setPopup('forward-to-selected')} className='green'>העבר</div>
            </div>
            buttons.push(next)
        }

        if (!buttons.length) return null
        return <div className='buttons-wrapper'>
            <p>{isAdmin ? 'אפשרויות' : 'הגש לבדיקה'}</p>
            <div className='buttons'>
                {buttons.map(button => button)}
            </div>
        </div>
    }, [isEditable, changeFlag, isAdmin, createdBy.role])

    const handleButtonClick = async (ns) => {
        setPopup('')
        if (ns === 'discard-changes') {
            setEdited(original.current)
            setChangeFlag(false)
            return
        }
        if (ns === 'save-thumbnails') return saveThumbnails(false)
        if (ns === 'finish-thumbnails') return saveThumbnails(true)

        if (!edited.titles[0]) return dispatch(setUpperPopup('empty-title'))
        dispatch(setUpperPopup('loader'))
        try {
            const thumbnails = await getThumbnailsUrls()
            const e = { ...edited, thumbnails }
            const nextSteps = ns === 'forward-to-selected' ? 'forward-to-' + nextMember.current.value : ns
            const { ideaMaker } = await videoService.editVideo({ partToEdit: 'ideaMaker', videoId, teamId: currentTeam._id, edit: e, nextSteps })
            original.current = ideaMaker
            dispatch(setUpperPopup('video-edited'))
            setChangeFlag(false)
            if (nextSteps === 'save-only') setEdited(ideaMaker)
            else navigate('/')
        }
        catch {
            dispatch(setUpperPopup('errorServer'))
        }
    }

    const getThumbnailsUrls = async () => {
        const thumbnailPromises = [
            files[0] === part.thumbnails[0] ? Promise.resolve(files[0] ? { secure_url: part.thumbnails[0] } : { secure_url: '' }) : uploadFile(files[0]),
            files[1] === part.thumbnails[1] ? Promise.resolve(files[1] ? { secure_url: part.thumbnails[1] } : { secure_url: '' }) : uploadFile(files[1]),
            files[2] === part.thumbnails[2] ? Promise.resolve(files[2] ? { secure_url: part.thumbnails[2] } : { secure_url: '' }) : uploadFile(files[2])
        ]
        const [thumbnail1, thumbnail2, thumbnail3] = await Promise.all(thumbnailPromises)
        return [thumbnail1.secure_url, thumbnail2.secure_url, thumbnail3.secure_url]
    }

    return <form id='ideaMaker' onInput={(ev) => { if (ev.target.name !== 'forward-to-selected') setChangeFlag(true) }}>
        {isOpen && <div className='main3-back'>
            <div className='idea-maker'>
                <div className='right'>
                    <h3>כותרות</h3>
                    <div className="numbered">
                        <p>1</p>
                        <input maxLength={70} style={{ borderWidth: '3px' }} type="text" placeholder="כותרת 1" className="simple-input title" value={edited.titles[0]} onChange={editTitle} readOnly={!isEditable} name="0" />
                    </div>
                    {edited.titles[0] && <div className="numbered">
                        <p>2</p>
                        <input maxLength={70} type="text" placeholder="כותרת 2" className="simple-input title" value={edited.titles[1]} onChange={editTitle} readOnly={!isEditable} name="1" />
                    </div>}
                    {edited.titles[1] && <div className="numbered">
                        <p>3</p>
                        <input maxLength={70} type="text" placeholder="כותרת 3" className="simple-input title" value={edited.titles[2]} onChange={editTitle} readOnly={!isEditable} name="2" />
                    </div>}

                    <h3>תיאור</h3>
                    <textarea className='area' placeholder="תיאור קצר על הסרטון" readOnly={!isEditable} value={edited.description} onChange={(ev) => setEdited({ ...edited, description: ev.target.value })} />

                    <h3>סרטון השראה</h3>
                    <div className='link-wrapper'>
                        <input readOnly={!isEditable} type='url' value={edited.exampleVideoLink} placeholder='לינק לסרטון השראה' onChange={(ev) => setEdited({ ...edited, exampleVideoLink: ev.target.value })} />
                        {edited.exampleVideoLink && <p className='underline' onClick={() => window.open(edited.exampleVideoLink, '_blank')}>צפה</p>}
                    </div>
                </div>


                <div className='left'>
                    <h3>מיקום</h3>
                    <input type="text" placeholder="מיקום" className="simple-input" value={edited.location} onChange={(ev) => setEdited({ ...edited, location: ev.target.value })} readOnly={!isEditable} />

                    <h3>טאמבנייל</h3>
                    <Thumbnails thumbnails={edited.thumbnails} setPopup={setPopup} />

                    <h3>תיאור טאמבנייל</h3>
                    <textarea className='area' readOnly={!isEditable} value={edited.thumbnailDescription} onChange={(ev) => setEdited({ ...edited, thumbnailDescription: ev.target.value })} />

                </div>
            </div>

            {(status.status !== 'started' && status.level === 'ideaMaker') && <>
                <h3 className='main7'>משוב - יוצר רעיונות</h3>
                <textarea className='area' placeholder="משוב מאדמין הצוות" value={edited.feedback} onChange={(ev) => setEdited({ ...edited, feedback: ev.target.value })} readOnly={!isAdmin} />
            </>}

            {popup === 'images' && <>
                <div className="screen blur" onClick={() => setPopup('')}></div>
                <section className='popup thumbnails-wrapper'>
                    <div className='img-wrapper'>
                        {((isEditable || isThumbnailEditor) && edited.thumbnails[0]) && <div className="delete-absolute" onClick={() => { files[0] = ''; setEdited({ ...edited, thumbnails: edited.thumbnails.map((thumbnail, idx) => idx === 0 ? '' : thumbnail) }); setChangeFlag(true) }}><span className="material-symbols-outlined">delete</span></div>}
                        {(isEditable || isThumbnailEditor) && <input type="file" className="non-appear" accept="image/*" id='0' onChange={onImageChange} />}
                        <label htmlFor='0' className="main-thumbnail">
                            {edited.thumbnails[0] ? <img src={edited.thumbnails[0]} alt='thumbnail1' /> : <span className="material-symbols-outlined">{isEditable || isThumbnailEditor ? 'add' : ''}</span>}
                        </label>
                    </div>

                    <div className='img-wrapper'>
                        {((isEditable || isThumbnailEditor) && edited.thumbnails[1]) && <div className="delete-absolute" onClick={() => { files[1] = ''; setEdited({ ...edited, thumbnails: edited.thumbnails.map((thumbnail, idx) => idx === 1 ? '' : thumbnail) }); setChangeFlag(true) }}><span className="material-symbols-outlined">delete</span></div>}
                        {(isEditable || isThumbnailEditor) && <input type="file" className="non-appear" accept="image/*" id='1' onChange={onImageChange} />}
                        <label htmlFor='1'>
                            {edited.thumbnails[1] ? <img src={edited.thumbnails[1]} alt='thumbnail1' /> : <span className="material-symbols-outlined">{isEditable || isThumbnailEditor ? 'add' : ''}</span>}
                        </label>
                    </div>

                    <div className='img-wrapper'>
                        {((isEditable || isThumbnailEditor) && edited.thumbnails[2]) && <div className="delete-absolute" onClick={() => { files[2] = ''; setEdited({ ...edited, thumbnails: edited.thumbnails.map((thumbnail, idx) => idx === 2 ? '' : thumbnail) }); setChangeFlag(true) }}><span className="material-symbols-outlined">delete</span></div>}
                        {(isEditable || isThumbnailEditor) && <input type="file" className="non-appear" accept="image/*" id='2' onChange={onImageChange} />}
                        <label htmlFor='2'>
                            {edited.thumbnails[2] ? <img src={edited.thumbnails[2]} alt='thumbnail2' /> : <span className="material-symbols-outlined">{isEditable || isThumbnailEditor ? 'add' : ''}</span>}
                        </label>
                    </div>
                </section>
            </>}
            {(popup && popup !== 'images') && <VideoPopup close={() => setPopup('')} type={popup} onConfirm={handleButtonClick} />}
            {buttons}
        </div>
        }
    </form >
}

function Thumbnails({ thumbnails, setPopup }) {
    if (!thumbnails.some(thumbnail => thumbnail !== '')) return <div className='thumbnails-click clickable' onClick={() => setPopup('images')}>
        <NoThumbnail />
    </div>
    return <div className='thumbnails-click' onClick={() => setPopup('images')}>
        {thumbnails.map((thumbnail, idx) => thumbnail ? <img src={thumbnail} alt='thumbnail' key={idx} /> : null)}
    </div>
}